import type { GetServerSideProps, InferGetServerSidePropsType, Metadata } from 'next';
import { NextSeo } from 'next-seo';
import { getRequestPromise } from '@qb/httpRequest/RequestPromise';
import { HUBSPOT_ACCESS_TOKEN } from '@/WebConstants';
import { AllBlogPostsResponse, BlogAuthorResponse } from '@/shared/types/common/BlogTypes';
import { Blog } from '@/src/components/Blog/Blog';
import { getReadTimeFromHtml } from '@/src/components/Blog/BlogPost/utils/getReadTimeFromHtml';
export const BLOG_API_URL = 'https://api.hubapi.com/cms/v3/blogs';
export const BLOG_API_REQ_HEADERS = {
  headers: {
    Authorization: `Bearer ${HUBSPOT_ACCESS_TOKEN}`,
    'Content-Type': 'application/json'
  }
};
export type BlogPageProps = {
  posts: {
    total: AllBlogPostsResponse['total'];
    results: Array<AllBlogPostsResponse['results'][0] & {
      postReadTime: number;
      authorFetchedName: string;
      authorAvatar: string;
    }>;
  };
};
export const metadata: Metadata = {
  title: `Quotebeam's Blog: The Second Shift`,
  description: `Explore Quotebeam's blog: Essential insights for suppliers on enhancing customer experience, and spotlighting engineers' remarkable achievements. Dive in!`
};
const BlogPage = ({
  posts
}: InferGetServerSidePropsType<typeof getServerSideProps>) => {
  return <>
      <NextSeo title={(metadata.title as string)} description={(metadata.description as string)} data-sentry-element="NextSeo" data-sentry-source-file="index.tsx" />
      <Blog posts={posts} data-sentry-element="Blog" data-sentry-source-file="index.tsx" />
    </>;
};
export default BlogPage;
export const getServerSideProps: GetServerSideProps<BlogPageProps> = async () => {
  const allPosts = await getRequestPromise<AllBlogPostsResponse>(`${BLOG_API_URL}/posts?sort=-publishDate&state=PUBLISHED&limit=100`, BLOG_API_REQ_HEADERS);
  const uniqueBlogAuthorIDs = new Set(allPosts.results.map(post => post.blogAuthorId));
  const authorsDataPromises = Array.from(uniqueBlogAuthorIDs).map(authorID => getRequestPromise<BlogAuthorResponse>(`${BLOG_API_URL}/authors/${authorID}`, BLOG_API_REQ_HEADERS));
  const authorsData = await Promise.all(authorsDataPromises);
  const enhancedPosts = allPosts.results.map(post => {
    const authorData = authorsData.find(author => author.id === post.blogAuthorId);
    return {
      ...post,
      postReadTime: getReadTimeFromHtml(post.postBody),
      authorFetchedName: authorData?.displayName || post.authorName,
      authorAvatar: authorData?.avatar || ''
    };
  });
  return {
    props: {
      posts: {
        total: allPosts.total,
        results: enhancedPosts
      }
    }
  };
};